import clsx from 'clsx';
import {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { RESIZE_CURSOR } from '../icons';

const KEYBOARD_STEP_PCT = 2;

export const clampPct = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

interface Drag {
  startX: number;
  startPct: number;
  containerWidth: number;
  prevUserSelect: string;
  prevCursor: string;
  frame: number | null;
  pendingPct: number;
}

interface Props {
  containerRef: RefObject<HTMLElement | null>;
  rightPct: number;
  minPct: number;
  maxPct: number;
  defaultPct?: number;
  onChange: (pct: number) => void;
  label?: string;
}

export function ResizeHandle(props: Props) {
  const {
    containerRef,
    rightPct,
    minPct,
    maxPct,
    defaultPct = minPct,
    onChange,
    label = 'Resize panel',
  } = props;

  const dragRef = useRef<Drag | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (drag.frame !== null) cancelAnimationFrame(drag.frame);
    document.body.style.userSelect = drag.prevUserSelect;
    document.body.style.cursor = drag.prevCursor;
    setIsDragging(false);
  }, []);

  useEffect(() => endDrag, [endDrag]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || e.ctrlKey) return;
    const containerWidth =
      containerRef.current?.getBoundingClientRect().width ?? 0;
    if (containerWidth === 0) return;
    e.preventDefault();
    e.currentTarget.focus();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startPct: rightPct,
      containerWidth,
      prevUserSelect: document.body.style.userSelect,
      prevCursor: document.body.style.cursor,
      frame: null,
      pendingPct: rightPct,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = RESIZE_CURSOR;
    setIsDragging(true);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaPct = ((e.clientX - drag.startX) / drag.containerWidth) * 100;
    drag.pendingPct = clampPct(drag.startPct - deltaPct, minPct, maxPct);
    if (drag.frame !== null) return;
    drag.frame = requestAnimationFrame(() => {
      drag.frame = null;
      onChange(drag.pendingPct);
    });
  };

  const onPointerUp = () => {
    const drag = dragRef.current;
    if (!drag) return;
    const flush = drag.frame !== null ? drag.pendingPct : null;
    endDrag();
    if (flush !== null) onChange(flush);
  };

  const reset = () => onChange(clampPct(defaultPct, minPct, maxPct));

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(clampPct(rightPct + KEYBOARD_STEP_PCT, minPct, maxPct));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange(clampPct(rightPct - KEYBOARD_STEP_PCT, minPct, maxPct));
    } else if (e.key === 'Home') {
      e.preventDefault();
      reset();
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(clampPct(rightPct, minPct, maxPct))}
      aria-valuemin={minPct}
      aria-valuemax={maxPct}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      className={clsx(
        "relative z-10 flex-none border-l-2 outline-none before:absolute before:-inset-x-1.5 before:inset-y-0 before:content-['']",
        isDragging
          ? 'border-onlight-primary dark:border-ondark-primary'
          : 'border-onlight-line-3 dark:border-ondark-line-2 hover:border-onlight-primary focus-visible:border-onlight-primary dark:hover:border-ondark-primary dark:focus-visible:border-ondark-primary'
      )}
      style={{ cursor: RESIZE_CURSOR }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onLostPointerCapture={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={reset}
    />
  );
}
